<template>
  <div class="form">
    <div class="row form-group">
      <label class="col-sm-3 col-form-label">
        {{ $t('Phone number') }}
      </label>
      <div class="col-sm-8">
        <div class="input-group flex-nowrap">
          <div class="input-group-prepend">
            <div class="input-group-text">
              <font-awesome-icon icon="phone-alt"/>
            </div>
          </div>
          <phone-number-input v-model="phone" :phoneNumber="phoneNumber"></phone-number-input>
        </div>
        <div class="d-block" v-if="phoneNumberError">
          <small class="form-text text-danger">{{ phoneNumberError }}</small>
        </div>
        <div class="form-check pt-2 pb-2">
          <label class="form-check-label"
                 :title="!phone.number || phoneNumberInvalid ? $t('Callback will be available when you enter valid phone number') : ''"
          >
            <input type="checkbox"
                   class="form-check-input"
                   v-model="callBack"
                   :disabled="!phone.number || phoneNumberInvalid"
            >
            <div style="cursor: pointer">{{ $t('CALLBACK') }}
              <small class="text-muted">
                {{ !phone.number || phoneNumberInvalid ? $t('Valid phone number needed') : $t('Receive a call when driver arrives') }}
              </small>
            </div>
          </label>
        </div>
      </div>
    </div>
    <div class="row">
      <label class="col-sm-3 col-form-label">
        {{ $t('Travellers') }}
      </label>
      <div class="col-md-5 col-sm-8">
        <div class="row">
          <div class="col">
            <ul class="list-unstyled">
              <li class="pb-2" v-for="key in travellerKeys">
                <label>
                  <input type="checkbox"
                         v-model="travellers[key].checked"
                         :disabled="travellers[key].disabled"
                         :aria-label="$t(key)"
                  > {{ $t(key) }}
                </label>
                <input type="number"
                       class="pull-right quantity"
                       v-model="travellers[key].quantity"
                       v-show="travellers[key].checked"
                       :disabled="travellers[key].disabled"
                       :aria-label="$t(key)"
                >
              </li>
            </ul>
          </div>
        </div>
      </div>
    </div>

    <div class="row form-group">
      <label class="col-sm-3 col-form-label">
        {{ $t('Wheelchair type') }}
      </label>
      <div class="col-sm-9">
        <div class="pb-2">
          <div class="form-check-inline" v-for="key in wheelchairKeys">
            <label class="form-check-label">
              <input type="radio"
                     class="form-check-input"
                     :value="key"
                     v-model="wheelchairType"
              > {{ $t(key) }}
            </label>
          </div>
        </div>
      </div>
    </div>
    <div class="row form-group">
      <div class="offset-sm-3 col-sm-9">
        <div class="pb-2">
          <div class="form-check form-check-inline" v-for="key in extraToKeys">
            <label class="form-check-label pb-2">
              <input class="form-check-input"
                     type="checkbox"
                     v-model="extraTo[key].checked"
              > {{ $t(key) }}
            </label>
          </div>
        </div>
      </div>
    </div>

    <div class="row form-group">
      <label class="col-sm-3 col-form-label">
        {{ $t('Note') }}
      </label>
      <div class="col-sm-9">
        <textarea class="form-control"
                  v-model="noteForDriver"
                  :aria-label="$t('Note')"
        ></textarea>
      </div>
    </div>
  </div>
</template>

<script>
  import PhoneNumberInput from '@/components/PhoneNumberInput';
  import HardcodedMap from '@/models/HardcodedMap'
  import { mapGetters } from 'vuex';

  export default {
    name: 'TripFormOptions',

    props: {
      tripType: {
        type: String,
        default: 'originalTrip'
      },
      withReturn: {
        type: Boolean,
        default: false
      },
      originalTrip: {
        type: Object,
        default() {
          return {}
        }
      },
      populateCustomerData: {
        type: Boolean,
        default: false
      }
    },

    components: {
      PhoneNumberInput,
    },

    computed: {
      ...mapGetters('account', [
        'ICustomer',
        'customerAddresses',
        'customerDebtorUid',
        'customerDefaultPaymentType',
        'customerPhoneNumber',
        'customerTransportObjects',
        'customerNoteForDriver',
      ]),
      phoneNumberInvalid() {
        return this.phone.number.trim().length > 0
          && !this.phone.valid;
      },
      phoneNumberError() {
        if( this.phone.number && !this.phone.valid ) {
          return this.$t('errors.enterValidPhoneNumber')
        }
        return null;
      },
      withCallback() {
        return this.phone.valid && this.callBack
      },
      isReady() {
        return _.compact([
          this.phoneNumberError
        ])
      },
      travellerKeys() {
        return _.keys(this.travellers)
      },
      wheelchairKeys() {
        return _.keys(this.wheelchairs)
      },
      wheelchairTo() {
        return this.wheelchairType ? {
          transportObjectUid: this.wheelchairs[this.wheelchairType].transportObjectUid,
          quantity: 1
        } : null
      },
      extraToKeys() {
        return _.keys(this.extraTo)
      },
      transportObjects() {
        return _.compact([
          ..._.transform(this.travellers, (acc, e)=>{
            if(e.checked) {
              acc.push({
                transportObjectUid: e.transportObjectUid,
                quantity: e.quantity,
              })
            }
          }, []),
          ..._.transform(this.extraTo, (acc, e)=>{
            if(e.checked) {
              acc.push({
                transportObjectUid: e.transportObjectUid,
                quantity: e.quantity,
              })
            }
          }, []),
          this.wheelchairTo,
        ])
      },
      tags() {
        return _.compact([
          ..._.transform(this.travellers, (acc, e)=>{
            if(e.checked) {
              acc.push(e.tagUid)
            }
          }, []),
          ..._.transform(this.extraTo, (acc, e)=>{
            if(e.checked) {
              acc.push(e.tagUid)
            }
          }, []),
          this.wheelchairType ? this.wheelchairs[this.wheelchairType].tagUid : null,
          this.withCallback ? this.callBackUid : null,
        ])
      }
    },

    data(){
      const {organizationUid}= this.$store.state.account
      const hardcodedMap= HardcodedMap[organizationUid] || {}
      if(_.isEmpty(hardcodedMap)) {
        // nije konfana mapa sa uid-ovima
      }

      // hardcoded stuff cloned and mapped
      const travellers= _.cloneDeep(hardcodedMap.travellers)
      const wheelchairs= _.cloneDeep(hardcodedMap.wheelchairs)
      const extraTo= _.cloneDeep(hardcodedMap.extraTo)
      const callBackUid= hardcodedMap.CALLBACK

      return {
        phone: {
          number: '',
          valid: false,
          invalid: false
        },
        phoneNumber: '',
        callBack: false,
        callBackUid,
        travellers,
        wheelchairType: '',
        wheelchairs,
        extraTo,
        noteForDriver: '',
      }
    },

    mounted() {
      if(this.populateCustomerData) {
        this.$log.debug(this.$options.name, `Populating customer data`)
        this.phoneNumber= this.customerPhoneNumber
        this.populateTransportObjects(this.customerTransportObjects)
        this.noteForDriver= _.compact([
          this.customerNoteForDriver,
          this.travellers.MEDICAL_ASSISTANCE.checked && 'Verplichte medische begeleiding'
          ]).join("\n")
      }
    },

    methods: {
      getData() {
        const data= {
          tripData: {
            transportObjects: this.transportObjects,
            tagUids: this.tags,
            tags: this.tags,
            noteForDriver: this.noteForDriver
          }
        };

        if(this.phone.valid) {
          data.customer= {
            phoneNumber: this.phone.number,
            callBackPhoneNumber: this.callBack ? this.phone.number : null
          }
        }
        return JSON.parse(JSON.stringify(data));
      },
      populateFromTrip(isReturn) {
        this.$log.debug(this.$options.name, JSON.parse(JSON.stringify(this.originalTrip)))
        const {customer, tripData} = this.originalTrip;
        this.noteForDriver= tripData.noteForDriver;
        this.phoneNumber = customer.callBackPhoneNumber || customer.phoneNumber || "";
        this.callBack= (customer.phoneNumber || customer.callBackPhoneNumber) && _.includes(tripData.tagUids, this.callBackUid);
        this.populateTransportObjects(tripData.transportObjects)
      },
      populateTransportObjects(transportObjects) {
        this.$log.debug(this.$options.name, `populating transport objects`, transportObjects);
        const self= this;
        _.forEach(transportObjects, ({transportObjectUid, quantity, addType}) =>{
          _.forEach(self.wheelchairs, (e, key) =>{
            if( transportObjectUid === e.transportObjectUid ) {
              self.wheelchairType = key
            }
          })
          _.forEach(self.travellers, (e, key) => {
            if( transportObjectUid === e.transportObjectUid ) {
              self.travellers[key].checked = self.originalTrip.tripNew === false || addType === 'AUTO'
              self.travellers[key].quantity = quantity
              self.travellers[key].disabled = false
              if( e.transportObjectUid === self.travellers.MEDICAL_ASSISTANCE.transportObjectUid ){
                // dodatna logika ovo ovog transportnog objekta
                self.travellers[key].disabled = addType === 'AUTO'
              }
            }
          })
          _.forEach(self.extraTo, (e, key) => {
            if( transportObjectUid === e.transportObjectUid ) {
              self.extraTo[key].checked = true;
            }
          })
        })
      },
    },

    watch: {
      isReady(errors) {
        this.$emit('input', errors.length === 0)
      },
      travellers: {
        deep: true,
          handler: function(travellers) {
            _.forEach(travellers, (e, key)=>{
              if(e.quantity < 1) {
                this.travellers[key].quantity = 1;
              } else if (e.quantity > e.maxQuantity) {
                this.travellers[key].quantity = e.maxQuantity;
              }
            })
        }
      },
      $data: {
        deep: true,
        handler() {
          // this.$log.debug('travellers wathcer changed', this.getData())
          this.$emit('update', this.getData())
        }
      },
      phone: {
        deep: true,
        handler() {
          this.$log.debug(this.$options.name, arguments)
        }
      },
      withReturn(withReturn) {
        if(withReturn) {
          this.populateFromTrip(true)
        }
      },
    }
  }
</script>

<style scoped>
  input.quantity {
    width: 55px;
    text-align: center;
    border: 1px solid #ced4da;
  }
</style>
